import uniq from "lodash/uniq";

import {
	type ComponentType,
	type Domain,
	type GroupRelation,
	type IndirectRelation,
	type Layer,
	type NodeId,
	type RelationType,
	type TypeGroup,
	type ViewComponent,
	type ViewRelation,
	type ViewSnapshot,
	type ViewSnapshotDiff,
	isComponentPathElement,
	isSingleNonDeterministicTopologyPath,
} from "~/models/dto";
import { ImmutableMap } from "~/tools/immutable-map";
import { isPresent } from "~/tools/is-present";
import { toArray } from "~/tools/to-array";

const applyElementsDiff = <TValue>(
	getId: (item: TValue) => NodeId,
	deleted: ReadonlyArray<NodeId>,
	added: ReadonlyArray<TValue>,
	updated: ReadonlyArray<TValue>,
	map: ReadonlyMap<NodeId, TValue>,
): Map<NodeId, TValue> => {
	const clone = new Map(map);

	deleted.forEach((id) => {
		clone.delete(id);
	});
	updated.forEach((item) => {
		clone.set(getId(item), item);
	});
	added.forEach((item) => {
		clone.set(getId(item), item);
	});

	return clone;
};

const applyRelationsDiff = <TRelation extends { id: NodeId; target: NodeId; source: NodeId }>(
	deleted: ReadonlyArray<TRelation>,
	added: ReadonlyArray<TRelation>,
	map: ReadonlyMap<NodeId, TRelation>,
): ReadonlyMap<NodeId, TRelation> => {
	const clone = new Map(map);

	clone.forEach((relation) => {
		const isDeleted = deleted.some(({ target, source }) => target === relation.target && source === relation.source);

		if (isDeleted) {
			clone.delete(relation.id);
		}
	});

	added.forEach((item) => {
		clone.set(item.id, item);
	});

	return clone;
};

export abstract class TopologySnapshot {
	public abstract readonly components: ReadonlyMap<NodeId, ViewComponent>;
	public abstract readonly relations: ReadonlyMap<NodeId, ViewRelation>;
	public abstract readonly groups: ReadonlyMap<NodeId, TypeGroup>;
	public abstract readonly groupRelations: ReadonlyMap<NodeId, GroupRelation>;
	public abstract readonly indirectRelations: ReadonlyMap<NodeId, IndirectRelation>;
	public abstract readonly componentTypes: ReadonlyMap<string, ComponentType>;
	public abstract readonly relationTypes: ReadonlyMap<string, RelationType>;
	public abstract readonly layers: ReadonlyArray<Layer>;
	public abstract readonly domains: ReadonlyArray<Domain>;
	public abstract readonly groupedByLayers: boolean;
	public abstract readonly groupedByDomains: boolean;

	public static fromViewSnapshot({
		components,
		relations,
		indirectRelations,
		groups,
		groupRelations,
		metadata: { componentTypes, relationTypes, layers, domains, groupedByLayers, groupedByDomains },
	}: ViewSnapshot): TopologySnapshot {
		return {
			components: ImmutableMap.fromList(({ id }) => id!, components),
			relations: ImmutableMap.fromList(({ id }) => id!, relations),
			groups: ImmutableMap.fromList(({ id }) => id, groups),
			groupRelations: ImmutableMap.fromList(({ id }) => id, groupRelations),
			indirectRelations: ImmutableMap.fromList(({ id }) => id, indirectRelations),
			componentTypes: ImmutableMap.fromList(({ identifier }) => identifier!, componentTypes),
			relationTypes: ImmutableMap.fromList(({ identifier }) => identifier!, relationTypes),
			layers,
			domains,
			groupedByLayers,
			groupedByDomains,
		};
	}

	public static applyDiff(diff: ViewSnapshotDiff, snapshot: TopologySnapshot): TopologySnapshot {
		const components = applyElementsDiff(
			({ id }) => id!,
			diff.deletedComponents,
			diff.addedComponents,
			diff.updatedComponents,
			snapshot.components,
		);
		const relations = applyElementsDiff(
			({ id }) => id!,
			diff.deletedRelations,
			diff.addedRelations,
			diff.updatedRelations,
			snapshot.relations,
		);
		const groups = applyElementsDiff(
			({ id }) => id,
			diff.deletedGroups,
			diff.addedGroups,
			diff.updatedGroups,
			snapshot.groups,
		);
		const groupRelations = applyRelationsDiff(
			diff.deletedGroupRelations,
			diff.addedGroupRelations,
			snapshot.groupRelations,
		);
		const indirectRelations = applyRelationsDiff(
			diff.deletedIndirectRelations,
			diff.addedIndirectRelations,
			snapshot.indirectRelations,
		);

		return {
			components,
			relations,
			groups,
			groupRelations,
			indirectRelations,
			componentTypes: ImmutableMap.fromList(({ identifier }) => identifier!, diff.metadata.componentTypes),
			relationTypes: ImmutableMap.fromList(({ identifier }) => identifier!, diff.metadata.relationTypes),
			layers: diff.metadata.layers,
			domains: diff.metadata.domains,
			groupedByLayers: diff.metadata.groupedByLayers,
			groupedByDomains: diff.metadata.groupedByDomains,
		};
	}

	public static getVisibleComponents(snapshot: TopologySnapshot): Array<ViewComponent> {
		return toArray(snapshot.components.values());
	}

	public static getIndirectRelationComponents(
		indirectRelationId: NodeId,
		snapshot: TopologySnapshot,
	): undefined | Array<NodeId> {
		const indirectRelation = snapshot.indirectRelations.get(indirectRelationId);

		if (indirectRelation == null || !isSingleNonDeterministicTopologyPath(indirectRelation.paths)) {
			return undefined;
		}

		return indirectRelation.paths.path.elements.filter(isComponentPathElement).map(({ id }) => id as NodeId);
	}

	public static getGroupRelationComponents(
		groupRelationId: NodeId,
		snapshot: TopologySnapshot,
	): undefined | Array<NodeId> {
		const groupRelation = snapshot.groupRelations.get(groupRelationId);

		if (groupRelation == null) {
			return undefined;
		}

		const components = [
			...groupRelation.indirectRelations.map((id) => snapshot.indirectRelations.get(id)),
			...groupRelation.directRelations.map((id) => snapshot.relations.get(id)),
		]
			.filter(isPresent)
			.flatMap((relation) => [relation.source, relation.target]);

		return uniq(components);
	}
}
